// src/components/common/aia/FTUEPanel.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

const FTUEPanel = ({
    tour,
    currentStep,
    canProceed = true,
    requireClick = false,
    requireValue = false,
    sectionLabel = 'General',
    sectionStepNumber = 1,
    sectionStepCount = 1,
    sectionProgress = 0,
    canSkipSection = false,
    inlinePanel = false,
    targetSelector = null,
    panelPosition = 'bottom',
    onNext,
    onPrevious,
    onSkipSection,
    onSkip,
    onComplete
}) => {
    const { theme } = useTheme();
    const step = tour?.steps[currentStep];
    const [inlineStyle, setInlineStyle] = useState(null);

    useEffect(() => {
        if (!inlinePanel || !targetSelector) {
            setInlineStyle(null);
            return;
        }

        const placePanel = () => {
            const target = document.querySelector(targetSelector);
            if (!target) {
                setInlineStyle(null);
                return;
            }

            const rect = target.getBoundingClientRect();
            const panelWidth = Math.min(420, window.innerWidth - 24);
            const gap = 14;
            let left = rect.right + gap;
            let top = rect.top;

            if (panelPosition === 'left') {
                left = rect.left - panelWidth - gap;
            }
            if (panelPosition === 'bottom') {
                top = rect.bottom + gap;
                left = rect.left;
            }
            if (panelPosition === 'top') {
                top = rect.top - 420;
                left = rect.left;
            }

            left = Math.max(12, Math.min(left, window.innerWidth - panelWidth - 12));
            top = Math.max(12, Math.min(top, window.innerHeight - 420 - 12));

            setInlineStyle({
                top: `${top}px`,
                left: `${left}px`,
                right: 'auto',
                bottom: 'auto',
                width: `${panelWidth}px`,
                maxWidth: `${panelWidth}px`,
                maxHeight: '420px'
            });
        };

        placePanel();
        window.addEventListener('resize', placePanel);
        window.addEventListener('scroll', placePanel, true);
        const interval = setInterval(placePanel, 250);

        return () => {
            window.removeEventListener('resize', placePanel);
            window.removeEventListener('scroll', placePanel, true);
            clearInterval(interval);
        };
    }, [inlinePanel, targetSelector, panelPosition]);

    if (!step) return null;

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === tour.steps.length - 1;
    const progress = sectionProgress;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className={`fixed z-[10000] max-w-md w-[calc(100vw-2rem)] rounded-2xl shadow-2xl border ${
                theme === 'dark'
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
            }`}
            style={inlineStyle || {
                bottom: '120px',
                right: '100px',
                maxHeight: '400px'
            }}
        >
            {/* Header with tour title */}
            <div className={`px-5 py-4 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        <h3 className={`font-semibold text-lg ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {step.title}
                        </h3>
                        {/* Progress indicator */}
                        <div className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            {sectionLabel}: Step {sectionStepNumber} of {sectionStepCount}
                        </div>
                    </div>
                    <button
                        onClick={onSkip}
                        className={`p-1 rounded-lg transition-colors ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-500'
                        }`}
                        title="Skip tour"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Progress bar */}
                <div className={`mt-3 h-1.5 rounded-full overflow-hidden ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                    <motion.div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            {/* Content */}
            <div className="p-5 max-h-60 overflow-y-auto">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className={`text-base leading-relaxed ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}
                    >
                        {step.content}
                    </motion.div>
                </AnimatePresence>

                {/* Optional action button */}
                {step.action && (
                    <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        onClick={step.action.onClick}
                        className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            step.action.primary
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                : theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                    >
                        {step.action.label}
                    </motion.button>
                )}

                {requireClick && !canProceed && (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-4 text-sm font-medium ${
                            theme === 'dark' ? 'text-amber-300' : 'text-amber-700'
                        }`}
                    >
                        Click the highlighted item to continue.
                    </motion.div>
                )}
                {requireValue && !canProceed && (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-4 text-sm font-medium ${
                            theme === 'dark' ? 'text-amber-300' : 'text-amber-700'
                        }`}
                    >
                        Enter a company name to continue.
                    </motion.div>
                )}
            </div>

            {/* Footer with navigation */}
            <div className={`px-5 py-4 border-t flex items-center justify-between ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                {/* Back button */}
                <button
                    onClick={onPrevious}
                    disabled={isFirstStep}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isFirstStep
                            ? 'opacity-40 cursor-not-allowed'
                            : theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-300'
                                : 'hover:bg-gray-100 text-gray-700'
                    }`}
                >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                </button>

                {/* Skip section button */}
                {!isLastStep && canSkipSection && (
                    <button
                        onClick={onSkipSection}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-300'
                                : 'hover:bg-gray-100 text-gray-700'
                        }`}
                    >
                        Skip Section
                    </button>
                )}

                {/* Next/Complete button */}
                {isLastStep ? (
                    <button
                        onClick={onComplete}
                        className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg text-sm font-medium transition-all shadow-lg shadow-green-500/20"
                    >
                        <CheckCircle2 className="w-4 h-4" />
                        Complete
                    </button>
                ) : (
                    <button
                        onClick={onNext}
                        disabled={!canProceed}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                            canProceed
                                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/20'
                                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        Next
                        <ChevronRight className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Tour brand badge */}
            <div className="absolute -top-3 -left-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white text-lg">🤖</span>
                </div>
            </div>
        </motion.div>
    );
};

export default FTUEPanel;
